import { useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Check, ArrowRight, Phone, ChevronRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { CTABand } from "../components/Shared";
import { SERVICE_TYPES, GOOGLE, BUSINESS } from "../lib/data";
import { AREA_DETAILS, AREA_WHY, getArea } from "../lib/areas";

export default function AreaDetail() {
  const { slug } = useParams();
  const area = getArea(slug);

  const jsonLd = useMemo(() => {
    if (!area) return null;
    const url = `https://wisphomecleaning.ca/areas/${area.slug}`;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "House Cleaning",
        name: `House Cleaning in ${area.name}`,
        description: area.lead,
        url,
        areaServed: { "@type": "City", name: area.name },
        provider: {
          "@type": "HouseCleaningService",
          name: "Wisp Home Cleaning Services",
          telephone: "+1-289-799-2690",
          email: "Office@wisphomecleaning.ca",
          url: "https://wisphomecleaning.ca/",
          image: "https://wisphomecleaning.ca/wisp-logo.png",
          priceRange: "$$",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: GOOGLE.rating,
            reviewCount: GOOGLE.count,
          },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://wisphomecleaning.ca/" },
          { "@type": "ListItem", position: 2, name: "Areas We Serve", item: "https://wisphomecleaning.ca/areas" },
          { "@type": "ListItem", position: 3, name: area.name, item: url },
        ],
      },
    ];
  }, [area]);

  if (!area) return <Navigate to="/areas" replace />;

  return (
    <>
      <SEO
        title={`House Cleaning in ${area.name}`}
        description={`${area.lead} Wisp Home Cleaning serves ${area.region} — recurring, one-time, deep and move in/out cleaning. Book your free in-home visit: call or text 289-799-2690.`}
        canonical={`/areas/${area.slug}`}
        jsonLd={jsonLd}
        image={area.image}
      />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-white px-5 pt-10 pb-14 md:px-10">
        <div className="mx-auto max-w-7xl">
          <nav className="mb-6 flex items-center gap-1.5 text-sm text-slate-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/areas" className="hover:text-primary">Areas We Serve</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-slate-600">{area.name}</span>
          </nav>
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                <MapPin className="h-4 w-4" /> {area.region}
              </span>
              <h1 className="mt-3 font-heading text-4xl font-light tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
                {area.headline}
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">{area.lead}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="area-call-button"
                  className="group flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" /> Call or Text
                </a>
                <Link
                  to="/contact"
                  data-testid="area-contact-button"
                  className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-200 px-7 py-3.5 font-semibold text-slate-700 transition-all duration-300 hover:border-primary hover:text-primary"
                >
                  Book a free visit <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden rounded-3xl border border-slate-200 shadow-lg"
            >
              <img
                src={area.image}
                alt={`Clean, bright home in ${area.name}`}
                className="h-[300px] w-full object-cover lg:h-[380px]"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Body + neighbourhoods */}
      <section className="px-5 py-16 md:px-10">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-medium text-slate-900 md:text-3xl">
              Home cleaning across {area.name}
            </h2>
            {area.body.map((p, i) => (
              <p key={i} className="mt-4 text-lg leading-relaxed text-slate-600">{p}</p>
            ))}

            <h3 className="mt-10 font-heading text-lg font-semibold text-slate-900">Neighbourhoods we serve in {area.name}</h3>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {area.neighbourhoods.map((n) => (
                <span
                  key={n}
                  data-testid="area-neighbourhood"
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                >
                  <MapPin className="h-3.5 w-3.5 text-primary" /> {n}
                </span>
              ))}
            </div>
          </div>

          {/* Why Wisp */}
          <aside className="rounded-3xl border border-slate-200 bg-muted p-7">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Why {area.name} families choose Wisp</h3>
            <ul className="mt-5 space-y-3">
              {AREA_WHY.map((w) => (
                <li key={w} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {w}
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl bg-white p-4 text-center">
              <p className="font-heading text-2xl font-semibold text-slate-900">{GOOGLE.rating}★</p>
              <p className="text-xs text-slate-500">{GOOGLE.count} Google reviews</p>
            </div>
          </aside>
        </div>
      </section>

      {/* Services recap */}
      <section className="border-y border-slate-200 bg-white px-5 py-16 md:px-10">
        <div className="mx-auto max-w-7xl">
          <h2 className="text-center font-heading text-2xl font-medium text-slate-900 md:text-3xl">
            Cleaning services in {area.name}
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_TYPES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="rounded-3xl border border-slate-200 p-6"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Area FAQ */}
      <section className="px-5 py-16 md:px-10">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center font-heading text-2xl font-medium text-slate-900 md:text-3xl">
            {area.name} cleaning — good to know
          </h2>
          <div className="mt-8 space-y-4">
            {area.faqs.map((f) => (
              <div key={f.q} data-testid="area-faq" className="rounded-2xl border border-slate-200 bg-white p-6">
                <h3 className="font-heading text-lg font-semibold text-slate-900">{f.q}</h3>
                <p className="mt-2 leading-relaxed text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby areas */}
      <section className="border-t border-slate-200 bg-muted px-5 py-14 md:px-10">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Other areas we serve</h2>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {AREA_DETAILS.filter((a) => a.slug !== area.slug).map((a) => (
              <Link
                key={a.slug}
                to={`/areas/${a.slug}`}
                data-testid={`nearby-area-${a.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-primary hover:text-primary"
              >
                {a.name} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
